// src/pages/calidad/PpniPncReporte.jsx
// KPI 5.6 PPNI y PNC con disposición — pestañas Resumen / Histórico / Análisis / Historial.
// KPI = kg con disposición ÷ kg totales con desviación.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import * as XLSX from 'xlsx';
import { FiDownload, FiInfo, FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiExternalLink, FiEdit2, FiX } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';
import PpniPncFormulario, { VACIO, faltantes } from './PpniPncFormulario';

Chart.register(...registerables);

const BASE = '/api/calidad/ppni-pnc';
const COLOR = { verde: '#3E7C5A', amarillo: '#B8862E', rojo: '#B23A48', morado: '#4A1E5C', lila: '#A98BB8', gris: '#8A7690', azul: '#4F7CA8', naranja: '#B2662A' };
const COLOR_DISP = { 'Destrucción': '#B23A48', 'Uso alterno': '#4F7CA8', 'Reproceso': '#B8862E', 'Retrabajo': '#A98BB8', 'Devolución al proveedor': '#6E4F80', 'Autorización de uso': '#3E7C5A', Pendiente: '#D9CDE0' };
const AGRUPAR = [
  ['tipo_desviacion', 'Tipo de desviación'], ['area_proceso', 'Área / proceso'], ['producto', 'Producto'], ['proveedor', 'Proveedor'],
  ['disposicion', 'Disposición'], ['clasificacion', 'Clasificación'], ['tipo_producto', 'Tipo de producto'], ['turno', 'Turno'], ['cual_pcc', 'PCC'],
];

const num = (v) => (v === null || v === undefined || v === '' ? null : parseFloat(v));
const fmtPct = (v) => (num(v) === null ? '—' : `${num(v).toFixed(1)}%`);
const fmtKg = (v) => (num(v) === null ? '—' : `${num(v).toLocaleString('es-MX', { maximumFractionDigits: 2 })} kg`);
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const pad = (n) => String(n).padStart(2, '0');
const aISO = (d) => { const x = new Date(d); return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`; };
const nombreMes = (d) => new Date(d).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
const mesCorto = (d) => new Date(d).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' });
function rangoDeMes(mes) {
  const d = new Date(mes);
  return { desde: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`, hasta: aISO(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
}
function semaforo(v, meta) {
  if (num(v) === null) return 'gris';
  if (num(v) >= meta) return 'verde';
  if (num(v) >= meta - 5) return 'amarillo';
  return 'rojo';
}

function Grafica({ config, alto = 260 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return <div style={{ position: 'relative', height: alto }}><canvas ref={ref} /></div>;
}

const TagClas = ({ c }) => (c ? <span style={{ ...x.tag, ...(c === 'PPNI' ? { color: COLOR.rojo, background: '#FDF3F1' } : { color: COLOR.naranja, background: '#FFF6E9' }) }}>{c}</span> : null);
const TagDisp = ({ d }) => (
  <span style={{ ...x.tag, color: d ? '#fff' : COLOR.rojo, background: d ? (COLOR_DISP[d] || COLOR.gris) : '#FDF3F1' }}>{d || 'Pendiente'}</span>
);

function filasExcel(registros) {
  return registros.map((r) => ({
    Folio: r.folio, 'Fecha de detección': fmtFecha(r.fecha_deteccion), Mes: nombreMes(r.fecha_deteccion),
    Clasificación: r.clasificacion_nombre || '', 'Tipo de producto': r.tipo_producto || '', 'Tipo de desviación': r.tipo_desviacion || '',
    'Pertenece a un PCC': r.pertenece_pcc || '', '¿Cuál PCC?': r.cual_pcc || '', '¿Peligro físico?': r.peligro_fisico || '',
    'Nombre del producto': r.producto || '', Proveedor: r.proveedor || '', 'No. Lote': r.lote || '', 'Área / proceso': r.area_proceso || '',
    'Cantidad con la desviación (kg)': num(r.kg_desviacion), 'Total de muestra': num(r.total_muestra), '% que representa': num(r.pct_muestra),
    'Descripción de la desviación': r.descripcion || '', 'Número de NetSuite': r.numero_netsuite || '', '¿Quién captura?': r.quien_captura || '',
    'Turno de la desviación': r.turno || '', 'Nombre de la imagen': r.imagen_nombre || '', 'Liga de la imagen': r.imagen_link || '',
    Disposición: r.disposicion || 'Pendiente', 'Autorizó': r.firma_disposicion || '', 'Fecha de disposición': fmtFecha(r.dispuesto_en),
  }));
}

export default function PpniPncReporte() {
  const [tab, setTab] = useState('resumen');
  const [mensual, setMensual] = useState({ data: [], por_disposicion: [] });
  const [meta, setMeta] = useState(100);
  const [catalogos, setCatalogos] = useState(null);
  const [desvMes, setDesvMes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  // Análisis
  const hoy = new Date();
  const [rango, setRango] = useState({ desde: `${hoy.getFullYear()}-01-01`, hasta: aISO(hoy) });
  const [agruparPor, setAgruparPor] = useState('tipo_desviacion');
  const [agrupado, setAgrupado] = useState([]);
  const [cargandoAgr, setCargandoAgr] = useState(false);

  // Historial
  const filtrosVacios = { desde: '', hasta: '', clasificacion: '', tipo_desviacion: '', area_proceso: '', disposicion: '', producto: '', busqueda: '' };
  const [filtros, setFiltros] = useState(filtrosVacios);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const [historial, setHistorial] = useState({ data: [], total: 0, kg: 0, totalPaginas: 1 });
  const [cargandoHist, setCargandoHist] = useState(false);
  const [abierto, setAbierto] = useState(null);
  const [exportando, setExportando] = useState(false);
  const [editando, setEditando] = useState(null);

  async function cargarAgrupado(por = agruparPor, r = rango) {
    try {
      setCargandoAgr(true);
      const res = await api.get(`${BASE}/agrupado`, { params: { por, ...r } });
      setAgrupado(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoAgr(false);
    }
  }

  async function cargarHistorial(f = filtros, p = pagina, pp = porPagina) {
    try {
      setCargandoHist(true);
      const params = { pagina: p, porPagina: pp };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${BASE}/registros`, { params });
      setHistorial({ data: res.data.data, total: res.data.total, kg: res.data.kg, totalPaginas: res.data.totalPaginas });
      setAbierto(null);
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoHist(false);
    }
  }

  async function cargarMensual() {
    const [m, c] = await Promise.all([api.get(`${BASE}/mensual`, { params: { n: 12 } }), api.get(`${BASE}/catalogos`)]);
    setMensual(m.data);
    setMeta(m.data.meta ?? 100);
    setCatalogos(c.data.data);
    return m.data.data;
  }

  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        const meses = await cargarMensual();
        const cerrado = [...meses].reverse().find((m) => !m.es_parcial && m.registros > 0);
        if (cerrado) {
          api.get(`${BASE}/agrupado`, { params: { por: 'tipo_desviacion', ...rangoDeMes(cerrado.mes) } }).then((r) => setDesvMes(r.data.data)).catch(() => {});
        }
        await Promise.all([cargarAgrupado('tipo_desviacion', rango), cargarHistorial(filtrosVacios, 1, porPagina)]);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el KPI de PPNI y PNC.');
      } finally {
        setCargando(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function irAHistorial(extra) {
    const f = { ...filtrosVacios, ...extra };
    setFiltros(f);
    setPagina(1);
    setTab('historial');
    cargarHistorial(f, 1);
  }

  async function exportarHistorial() {
    try {
      setExportando(true);
      const params = { exportar: '1' };
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${BASE}/registros`, { params });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasExcel(res.data.data)), 'Basedatos');
      XLSX.writeFile(wb, `PPNI_PNC_${filtros.desde || 'inicio'}_a_${filtros.hasta || 'hoy'}.xlsx`);
    } catch (err) {
      console.error(err);
    } finally {
      setExportando(false);
    }
  }

  // ─── Derivados ───
  const meses = mensual.data;
  const conDatos = meses.filter((m) => m.registros > 0);
  const cerrados = conDatos.filter((m) => !m.es_parcial);
  const mesCerrado = cerrados[cerrados.length - 1] || null;
  const mesPrev = cerrados[cerrados.length - 2] || null;
  const mesCurso = meses.find((m) => m.es_parcial) || null;
  const pendientesTotal = meses.reduce((t, m) => t + m.pendientes, 0);

  const configPct = useMemo(() => ({
    type: 'line',
    data: {
      labels: meses.map((m) => mesCorto(m.mes) + (m.es_parcial ? '*' : '')),
      datasets: [
        { label: '% kg con disposición', data: meses.map((m) => m.porcentaje), borderColor: COLOR.morado, backgroundColor: COLOR.morado, tension: 0.25, pointRadius: 5, spanGaps: true,
          pointBackgroundColor: meses.map((m) => (m.es_parcial ? '#fff' : COLOR[semaforo(m.porcentaje, meta)])) },
        { label: `Meta (${meta}%)`, data: meses.map(() => meta), borderColor: COLOR.verde, borderDash: [6, 4], borderWidth: 1.5, pointRadius: 0 },
      ],
    },
    options: { maintainAspectRatio: false, scales: { y: { suggestedMin: 80, max: 100, ticks: { callback: (v) => `${v}%` } } }, plugins: { legend: { position: 'bottom' } } },
  }), [meses, meta]);

  const configRegistros = useMemo(() => ({
    type: 'bar',
    data: {
      labels: meses.map((m) => mesCorto(m.mes) + (m.es_parcial ? '*' : '')),
      datasets: [
        { label: 'PPNI', data: meses.map((m) => m.ppni), backgroundColor: COLOR.rojo, stack: 'r' },
        { label: 'PNC', data: meses.map((m) => m.pnc), backgroundColor: COLOR.naranja, stack: 'r' },
      ],
    },
    options: { maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: 'Registros' } } }, plugins: { legend: { position: 'bottom' } } },
  }), [meses]);

  const configKgDisp = useMemo(() => {
    const disps = [...new Set(mensual.por_disposicion.map((d) => d.disposicion))];
    const clave = (m) => aISO(m);
    return {
      type: 'bar',
      data: {
        labels: meses.map((m) => mesCorto(m.mes) + (m.es_parcial ? '*' : '')),
        datasets: disps.map((d) => ({
          label: d,
          data: meses.map((m) => mensual.por_disposicion.find((p) => p.disposicion === d && clave(p.mes) === clave(m.mes))?.kg || 0),
          backgroundColor: COLOR_DISP[d] || COLOR.gris,
          stack: 'k',
        })),
      },
      options: { maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, title: { display: true, text: 'kg' } } }, plugins: { legend: { position: 'bottom' } } },
    };
  }, [meses, mensual.por_disposicion]);

  const configAgrupado = useMemo(() => {
    const top = agrupado.slice(0, 12);
    return {
      type: 'bar',
      data: {
        labels: top.map((r) => (r.nombre.length > 34 ? `${r.nombre.slice(0, 33)}…` : r.nombre)),
        datasets: [{ label: 'Registros', data: top.map((r) => r.registros), backgroundColor: agruparPor === 'disposicion' ? top.map((r) => COLOR_DISP[r.nombre] || COLOR.morado) : COLOR.morado }],
      },
      options: { indexAxis: 'y', maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { precision: 0 } } } },
    };
  }, [agrupado, agruparPor]);

  const configDesvMes = useMemo(() => ({
    type: 'bar',
    data: {
      labels: desvMes.slice(0, 8).map((r) => (r.nombre.length > 34 ? `${r.nombre.slice(0, 33)}…` : r.nombre)),
      datasets: [{ label: 'Registros', data: desvMes.slice(0, 8).map((r) => r.registros), backgroundColor: COLOR.morado }],
    },
    options: { indexAxis: 'y', maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { precision: 0 } } } },
  }), [desvMes]);

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;
  if (error) return <div style={{ ...s.paginaContenedor, color: COLOR.rojo }}>{error}</div>;

  const opcionesLista = (tipo) => (catalogos?.listas?.[tipo] || []).map((x) => x.valor);

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ ...s.headerFila, flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>5.6 PPNI y PNC con disposición</h1>
          <div style={{ fontSize: 12, color: COLOR.gris }}>F-CAL-05 · kg con disposición ÷ kg con desviación · meta {meta}%</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/calidad/ppni-pnc-captura" style={{ ...s.botonGuardarChico, textDecoration: 'none' }}>+ Capturar</Link>
          {pendientesTotal > 0 && <Link to="/calidad/ppni-pnc-pendientes" style={{ ...x.botonAlerta, textDecoration: 'none' }}>{pendientesTotal} pendiente(s)</Link>}
        </div>
      </div>

      {mensaje && <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? COLOR.verde : COLOR.rojo }}>{mensaje.texto}</div>}

      <div style={s.tabBar}>
        {[['resumen', 'Resumen'], ['historico', 'Histórico'], ['analisis', 'Análisis'], ['historial', 'Historial de registros']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...s.tabBtn, ...(tab === k ? s.tabBtnActivo : {}) }}>{label}</button>
        ))}
      </div>

      {/* ═══════════ RESUMEN ═══════════ */}
      {tab === 'resumen' && (
        <>
          <div style={s.kgrid}>
            <TarjetaPct titulo={mesCerrado ? `Mes cerrado · ${nombreMes(mesCerrado.mes)}` : 'Mes cerrado'} m={mesCerrado} previo={mesPrev} meta={meta} />
            <div style={{ ...s.kcard, borderTop: `3px dashed ${COLOR.gris}` }}>
              <div style={s.kLbl}>En curso {mesCurso && `· ${nombreMes(mesCurso.mes)}`}</div>
              <div style={{ ...s.kVal, fontSize: 30, color: '#2A1830' }}>{fmtPct(mesCurso?.porcentaje)}</div>
              <div style={s.kSub}>{mesCurso ? `${mesCurso.registros} registros · ${fmtKg(mesCurso.total_kg)} (parcial)` : ''}</div>
            </div>
            <div style={{ ...s.kcard, borderTop: `3px solid ${pendientesTotal ? COLOR.rojo : COLOR.verde}`, cursor: 'pointer' }} onClick={() => irAHistorial({ disposicion: 'PENDIENTE' })}>
              <div style={s.kLbl}>Sin disposición</div>
              <div style={{ ...s.kVal, fontSize: 30, color: pendientesTotal ? COLOR.rojo : COLOR.verde }}>{pendientesTotal}</div>
              <div style={s.kSub}>registros pendientes (últimos 12 meses). Clic para verlos.</div>
            </div>
            <div style={{ ...s.kcard, borderTop: `3px solid ${COLOR.morado}` }}>
              <div style={s.kLbl}>Registros {mesCerrado && `· ${nombreMes(mesCerrado.mes)}`}</div>
              <div style={{ ...s.kVal, fontSize: 30, color: COLOR.morado }}>{mesCerrado?.registros ?? 0}</div>
              <div style={s.kSub}>{mesCerrado ? `${mesCerrado.ppni} PPNI · ${mesCerrado.pnc} PNC · ${mesCerrado.peligro_fisico} con peligro físico · ${fmtKg(mesCerrado.kg_destruccion)} a destrucción` : ''}</div>
            </div>
          </div>

          <div style={s.grid2}>
            <div style={s.tarjeta}>
              <div style={s.seccionTitulo}>Registros por mes</div>
              <Grafica config={configRegistros} alto={240} />
            </div>
            <div style={s.tarjeta}>
              <div style={s.seccionTitulo}>Tipos de desviación {mesCerrado && `· ${nombreMes(mesCerrado.mes)}`}</div>
              {desvMes.length ? <Grafica config={configDesvMes} alto={Math.max(180, Math.min(8, desvMes.length) * 32 + 40)} /> : <div style={{ fontSize: 12, color: COLOR.gris }}>Sin datos.</div>}
            </div>
          </div>

          <div style={s.seccionKpiBloque}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <FiInfo style={{ color: COLOR.morado, marginTop: 2, flexShrink: 0 }} />
              <div style={s.seccionKpiFormula}>
                <strong style={{ color: '#2A1830' }}>Cómo se calcula.</strong> Suma de kg con desviación que ya tienen disposición ÷ suma de kg con desviación del periodo,
                por mes de la fecha de detección. Un registro sin disposición cuenta en contra hasta que se le asigna. Meta: {meta}%.
                <br />
                <strong style={{ color: '#2A1830' }}>PPNI</strong> = Producto Potencialmente No Inocuo · <strong style={{ color: '#2A1830' }}>PNC</strong> = Producto No Conforme.
                El histórico desde diciembre 2025 se cargó del Excel F-CAL-05.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ HISTÓRICO ═══════════ */}
      {tab === 'historico' && (
        <>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>% de kg con disposición</div>
            <Grafica config={configPct} />
            <div style={{ ...s.kSub, marginTop: 8 }}>* Mes en curso, parcial.</div>
          </div>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>kg por disposición</div>
            <Grafica config={configKgDisp} alto={260} />
          </div>
          <div style={{ ...s.headerFila, margin: '6px 0 8px' }}>
            <h3 style={s.subtituloLista}>Tabla mensual</h3>
            <button onClick={() => {
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([...meses].reverse().map((m) => ({
                Mes: nombreMes(m.mes), '% con disposición': m.porcentaje, Meta: meta, Registros: m.registros, PPNI: m.ppni, PNC: m.pnc,
                'kg con desviación': m.total_kg, 'kg con disposición': m.con_disposicion_kg, 'kg a destrucción': m.kg_destruccion,
                Pendientes: m.pendientes, 'Con peligro físico': m.peligro_fisico, 'En PCC': m.en_pcc, Parcial: m.es_parcial ? 'Sí' : '',
              }))), 'Mensual');
              XLSX.writeFile(wb, `PPNI_PNC_mensual_${aISO(new Date())}.xlsx`);
            }} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}><FiDownload /> Excel</button>
          </div>
          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Mes</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% con disposición</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Registros</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>PPNI</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>PNC</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>kg con desviación</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>A destrucción</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Pendientes</th>
                </tr>
              </thead>
              <tbody>
                {[...meses].reverse().map((m) => (
                  <tr key={m.mes} style={{ cursor: 'pointer' }} onClick={() => irAHistorial(rangoDeMes(m.mes))}>
                    <td style={s.td}>{nombreMes(m.mes)}{m.es_parcial && <span style={{ ...x.tag, color: '#7a5417', background: '#F9F0DD', marginLeft: 6 }}>parcial</span>}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: m.es_parcial ? COLOR.gris : COLOR[semaforo(m.porcentaje, meta)] }}>{fmtPct(m.porcentaje)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{m.registros}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{m.ppni}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{m.pnc}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{fmtKg(m.total_kg)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{fmtKg(m.kg_destruccion)}</td>
                    <td style={{ ...s.td, textAlign: 'right', color: m.pendientes ? COLOR.rojo : undefined, fontWeight: m.pendientes ? 700 : 400 }}>{m.pendientes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════ ANÁLISIS ═══════════ */}
      {tab === 'analisis' && (
        <>
          <div style={{ ...s.filtrosBar, alignItems: 'flex-end' }}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Ver por</span>
              <select style={s.filtroInput} value={agruparPor} onChange={(e) => { setAgruparPor(e.target.value); cargarAgrupado(e.target.value); }}>
                {AGRUPAR.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mes</span>
              <select style={s.filtroInput} value={meses.some((m) => rangoDeMes(m.mes).desde === rango.desde && rangoDeMes(m.mes).hasta === rango.hasta) ? rango.desde : ''}
                onChange={(e) => { const r = e.target.value ? rangoDeMes(`${e.target.value}T00:00:00`) : { desde: `${hoy.getFullYear()}-01-01`, hasta: aISO(hoy) }; setRango(r); cargarAgrupado(agruparPor, r); }}>
                <option value="">Rango</option>
                {[...conDatos].reverse().map((m) => <option key={m.mes} value={rangoDeMes(m.mes).desde}>{nombreMes(m.mes)}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} />
            </div>
            <button style={s.botonBuscar2} onClick={() => cargarAgrupado()}>Ver</button>
            <button disabled={!agrupado.length} onClick={() => {
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(agrupado.map((r) => ({
                [AGRUPAR.find((a) => a[0] === agruparPor)[1]]: r.nombre, Registros: r.registros, PPNI: r.ppni, PNC: r.pnc,
                'kg con desviación': r.total_kg, '% con disposición': r.porcentaje, Pendientes: r.pendientes, 'Con peligro físico': r.peligro_fisico, 'En PCC': r.en_pcc,
              }))), 'Análisis');
              XLSX.writeFile(wb, `PPNI_PNC_por_${agruparPor}_${rango.desde}_a_${rango.hasta}.xlsx`);
            }} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}><FiDownload /> Excel</button>
          </div>

          {cargandoAgr && <div style={s.subtitulo}>Cargando...</div>}
          {!cargandoAgr && agrupado.length === 0 && <div style={s.subtitulo}>Sin registros en este periodo.</div>}
          {!cargandoAgr && agrupado.length > 0 && (
            <>
              <div style={s.tarjeta}>
                <div style={s.seccionTitulo}>{AGRUPAR.find((a) => a[0] === agruparPor)[1]} con más registros</div>
                <Grafica config={configAgrupado} alto={Math.max(200, Math.min(12, agrupado.length) * 32 + 40)} />
              </div>
              <div style={s.subtitulo}>Haz clic en una fila para ver sus registros.</div>
              <div style={s.tablaScroll}>
                <table style={s.tabla}>
                  <thead>
                    <tr>
                      <th style={s.th}>{AGRUPAR.find((a) => a[0] === agruparPor)[1]}</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Registros</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>PPNI</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>PNC</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>kg con desviación</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Peligro físico</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Pendientes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agrupado.map((r) => {
                      const filtrable = ['tipo_desviacion', 'area_proceso', 'producto', 'clasificacion', 'disposicion'].includes(agruparPor) && r.nombre !== 'Sin dato';
                      const valor = agruparPor === 'disposicion' && r.nombre === 'Pendiente' ? 'PENDIENTE' : r.nombre;
                      return (
                        <tr key={r.nombre} style={{ cursor: filtrable ? 'pointer' : 'default' }} onClick={() => filtrable && irAHistorial({ ...rango, [agruparPor]: valor })}>
                          <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 220 }}>{agruparPor === 'disposicion' ? <TagDisp d={r.nombre === 'Pendiente' ? null : r.nombre} /> : r.nombre}</td>
                          <td style={{ ...s.td, textAlign: 'right', fontWeight: 700 }}>{r.registros}</td>
                          <td style={{ ...s.td, textAlign: 'right' }}>{r.ppni}</td>
                          <td style={{ ...s.td, textAlign: 'right' }}>{r.pnc}</td>
                          <td style={{ ...s.td, textAlign: 'right' }}>{fmtKg(r.total_kg)}</td>
                          <td style={{ ...s.td, textAlign: 'right', color: r.peligro_fisico ? COLOR.rojo : undefined }}>{r.peligro_fisico}</td>
                          <td style={{ ...s.td, textAlign: 'right', color: r.pendientes ? COLOR.rojo : undefined }}>{r.pendientes}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ═══════════ HISTORIAL ═══════════ */}
      {tab === 'historial' && (
        <>
          <div style={s.filtrosBar}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Clasificación</span>
              <select style={s.filtroInput} value={filtros.clasificacion} onChange={(e) => setFiltros({ ...filtros, clasificacion: e.target.value })}>
                <option value="">Ambas</option>
                <option value="PPNI">PPNI</option>
                <option value="PNC">PNC</option>
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Tipo de desviación</span>
              <select style={{ ...s.filtroInput, maxWidth: 220 }} value={filtros.tipo_desviacion} onChange={(e) => setFiltros({ ...filtros, tipo_desviacion: e.target.value })}>
                <option value="">Todos</option>
                {opcionesLista('tipo_desviacion').map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Área</span>
              <select style={s.filtroInput} value={filtros.area_proceso} onChange={(e) => setFiltros({ ...filtros, area_proceso: e.target.value })}>
                <option value="">Todas</option>
                {opcionesLista('area_proceso').map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Disposición</span>
              <select style={s.filtroInput} value={filtros.disposicion} onChange={(e) => setFiltros({ ...filtros, disposicion: e.target.value })}>
                <option value="">Todas</option>
                <option value="PENDIENTE">Pendiente</option>
                {opcionesLista('disposicion').map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Buscar</span>
              <input type="text" placeholder="Folio, producto, lote, descripción…" style={{ ...s.filtroInput, minWidth: 200 }} value={filtros.busqueda}
                onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPagina(1); cargarHistorial(filtros, 1); } }} />
            </div>
            <button style={s.botonBuscar2} onClick={() => { setPagina(1); cargarHistorial(filtros, 1); }}>Buscar</button>
            <button style={s.linkChico} onClick={() => { setFiltros(filtrosVacios); setPagina(1); cargarHistorial(filtrosVacios, 1); }}>Limpiar</button>
          </div>

          {filtros.producto && (
            <div style={{ ...s.avisoAdvertencia, marginTop: 0, marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
              Producto: <strong>{filtros.producto}</strong>
              <button style={s.linkChico} onClick={() => { const f = { ...filtros, producto: '' }; setFiltros(f); setPagina(1); cargarHistorial(f, 1); }}>quitar</button>
            </div>
          )}

          <div style={{ ...s.headerFila, marginBottom: 8 }}>
            <div style={{ fontSize: 12, color: COLOR.gris }}>{cargandoHist ? 'Cargando...' : `${historial.total.toLocaleString('es-MX')} registros · ${fmtKg(historial.kg)}`}</div>
            <button onClick={exportarHistorial} disabled={exportando || historial.total === 0} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiDownload /> {exportando ? 'Generando...' : 'Descargar Excel'}
            </button>
          </div>

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}></th>
                  <th style={s.th}>Folio</th>
                  <th style={s.th}>Fecha</th>
                  <th style={s.th}>Producto / lote</th>
                  <th style={s.th}>Desviación</th>
                  <th style={s.th}>Área</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>kg</th>
                  <th style={s.th}>Disposición</th>
                </tr>
              </thead>
              <tbody>
                {!cargandoHist && historial.data.length === 0 && <tr><td colSpan={8} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>Sin registros con estos filtros.</td></tr>}
                {historial.data.map((r) => {
                  const esAbierto = abierto === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr onClick={() => setAbierto(esAbierto ? null : r.id)} style={{ cursor: 'pointer', ...(!r.disposicion ? { background: '#FDF6F6' } : {}) }}>
                        <td style={s.td}>{esAbierto ? <FiChevronUp /> : <FiChevronDown />}</td>
                        <td style={{ ...s.td, fontWeight: 700, color: COLOR.morado }}>{r.folio}<div><TagClas c={r.clasificacion} /></div></td>
                        <td style={s.td}>{fmtFecha(r.fecha_deteccion)}{r.turno && <div style={x.sub}>{r.turno}</div>}</td>
                        <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 180 }}>{r.producto || '—'}{r.lote && <div style={x.sub}>Lote {r.lote}</div>}</td>
                        <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 180, fontSize: 12 }}>{r.tipo_desviacion || '—'}</td>
                        <td style={{ ...s.td, whiteSpace: 'normal' }}>{r.area_proceso || '—'}</td>
                        <td style={{ ...s.td, textAlign: 'right' }}>{fmtKg(r.kg_desviacion)}{r.pct_muestra !== null && <div style={x.sub}>{r.pct_muestra}% muestra</div>}</td>
                        <td style={s.td}><TagDisp d={r.disposicion} /></td>
                      </tr>
                      {esAbierto && (
                        <tr>
                          <td colSpan={8} style={x.celdaDetalle}>
                            {r.descripcion && <div style={{ fontSize: 13, color: '#2A1830', marginBottom: 10 }}>{r.descripcion}</div>}
                            <div style={x.detalleGrid}>
                              <Dato label="Clasificación" valor={r.clasificacion_nombre} />
                              <Dato label="Tipo de producto" valor={r.tipo_producto} />
                              <Dato label="Proveedor" valor={r.proveedor} />
                              <Dato label="¿Pertenece a un PCC?" valor={r.pertenece_pcc && `${r.pertenece_pcc}${r.cual_pcc && r.cual_pcc !== 'N/A' ? ` · ${r.cual_pcc}` : ''}`} />
                              <Dato label="¿Peligro físico (> 7 mm)?" valor={r.peligro_fisico} />
                              <Dato label="Total de muestra" valor={r.total_muestra !== null ? fmtKg(r.total_muestra) : null} />
                              <Dato label="Número de NetSuite" valor={r.numero_netsuite} />
                              <Dato label="¿Quién captura?" valor={r.quien_captura} />
                              <Dato label="Imagen" valor={r.imagen_nombre} />
                              <Dato label="Autorizó la disposición" valor={r.firma_disposicion} />
                            </div>
                            <div style={{ display: 'flex', gap: 14, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                              {r.imagen_link && <a href={r.imagen_link} target="_blank" rel="noreferrer" style={x.link}><FiExternalLink /> Ver imagen en SharePoint</a>}
                              <button style={{ ...x.botonEditar }} onClick={(e) => { e.stopPropagation(); setEditando(r); }}><FiEdit2 /> Editar registro</button>
                              {r.origen === 'excel' && <span style={x.sub}>Cargado del Excel F-CAL-05</span>}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={x.paginacion}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={s.filtroLabel}>Por página</span>
              <select style={s.inputChico} value={porPagina} onChange={(e) => { const pp = Number(e.target.value); setPorPagina(pp); setPagina(1); cargarHistorial(filtros, 1, pp); }}>
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={x.botonPagina} disabled={pagina <= 1 || cargandoHist} onClick={() => { const p = pagina - 1; setPagina(p); cargarHistorial(filtros, p); }} aria-label="Página anterior"><FiChevronLeft /></button>
              <span style={{ fontSize: 12 }}>Página {pagina} de {historial.totalPaginas}</span>
              <button style={x.botonPagina} disabled={pagina >= historial.totalPaginas || cargandoHist} onClick={() => { const p = pagina + 1; setPagina(p); cargarHistorial(filtros, p); }} aria-label="Página siguiente"><FiChevronRight /></button>
            </div>
          </div>
        </>
      )}

      {editando && (
        <EditarRegistro
          registro={editando}
          catalogos={catalogos}
          onCerrar={() => setEditando(null)}
          onGuardado={async (folio) => {
            setEditando(null);
            setMensaje({ tipo: 'ok', texto: `✓ Folio ${folio} actualizado.` });
            await Promise.all([cargarHistorial(), cargarMensual()]);
          }}
        />
      )}
    </div>
  );
}

function TarjetaPct({ titulo, m, previo, meta }) {
  const v = num(m?.porcentaje);
  const color = COLOR[semaforo(v, meta)];
  const p = num(previo?.porcentaje);
  const d = v !== null && p !== null ? v - p : null;
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${color}` }}>
      <div style={s.kLbl}>{titulo}</div>
      <div style={{ ...s.kVal, fontSize: 32, color }}>{fmtPct(v)}</div>
      {v !== null && <div style={{ fontSize: 12.5, fontWeight: 600, margin: '6px 0', color }}>{v >= meta ? `Cumple la meta de ${meta}%` : `No cumple la meta de ${meta}% (${(v - meta).toFixed(1)} pts)`}</div>}
      <div style={s.kSub}>{m ? `${fmtKg(m.con_disposicion_kg)} con disposición de ${fmtKg(m.total_kg)}` : 'Sin datos'}</div>
      {d !== null && <div style={{ ...s.kSub, color: d > 0 ? COLOR.verde : d < 0 ? COLOR.rojo : COLOR.gris }}>{d > 0 ? '▲' : d < 0 ? '▼' : '='} {Math.abs(d).toFixed(1)} pts vs mes anterior</div>}
    </div>
  );
}

const CAMPOS_EDITABLES = Object.keys(VACIO);

function EditarRegistro({ registro, catalogos, onCerrar, onGuardado }) {
  const inicial = useMemo(() => {
    const v = {};
    CAMPOS_EDITABLES.forEach((k) => {
      if (k === 'fecha_deteccion') v[k] = aISO(registro.fecha_deteccion);
      else if (k === 'kg_desviacion' || k === 'total_muestra') v[k] = registro[k] === null ? '' : String(num(registro[k]));
      else v[k] = registro[k] ?? '';
    });
    return v;
  }, [registro]);
  const [v, setV] = useState(inicial);
  const [intento, setIntento] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  async function guardar() {
    setIntento(true);
    const falta = faltantes(v);
    if (falta.length) { setError(`Falta llenar: ${falta.join(', ')}.`); return; }
    const cambios = Object.fromEntries(CAMPOS_EDITABLES.filter((k) => v[k] !== inicial[k]).map((k) => [k, v[k]]));
    if (!Object.keys(cambios).length) { onCerrar(); return; }
    try {
      setGuardando(true);
      setError(null);
      await api.put(`${BASE}/registros/${registro.id}`, cambios);
      onGuardado(registro.folio);
    } catch (err) {
      console.error(err);
      setError(err.response?.status === 403 ? 'No tienes permiso de captura en Calidad.' : err.response?.data?.error || 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div style={s.modalFondo} onClick={onCerrar}>
      <div style={{ ...s.modalCaja, maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitulo}>Editar folio {registro.folio}</div>
          <button style={s.modalCerrarBtn} onClick={onCerrar} aria-label="Cerrar"><FiX /></button>
        </div>
        <PpniPncFormulario valores={v} onCambio={setV} catalogos={catalogos} intento={intento} s={s} />
        {error && <div style={{ ...s.mensaje, color: COLOR.rojo, marginTop: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button onClick={guardar} disabled={guardando} style={s.botonGuardarChico}>{guardando ? 'Guardando...' : 'Guardar cambios'}</button>
          <button onClick={onCerrar} style={s.linkChico}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

const Dato = ({ label, valor }) => (
  <div>
    <div style={{ fontSize: 10, color: '#8A7690', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 12.5, color: '#2A1830' }}>{valor || '—'}</div>
  </div>
);

const x = {
  tag: { display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap', marginTop: 2 },
  sub: { fontSize: 10.5, color: '#8A7690' },
  celdaDetalle: { padding: '14px 18px 16px 44px', background: '#FAF6F0', borderBottom: '1px solid #F0E9E0' },
  detalleGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 },
  link: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#4A1E5C', fontWeight: 600 },
  botonEditar: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#6E4F80', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: 6, cursor: 'pointer' },
  botonAlerta: { padding: '6px 14px', fontSize: 12, fontWeight: 600, color: '#B23A48', background: '#FDF3F1', border: '1px solid #E8B4BB', borderRadius: 6 },
  paginacion: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  botonPagina: { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E6DDD2', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#4A1E5C' },
};